/**
 * DateParser utility for handling date conversions using seconds since 1970
 * Provides consistent date formatting and parsing throughout the application
 */
export class DateParser {
  /**
   * Convert a Date object to seconds since 1970
   * @param {Date} date - The date to convert
   * @returns {number} Seconds since 1970
   */
  static dateToSeconds(date) {
    if (!date || !(date instanceof Date)) {
      throw new Error('Invalid date provided to dateToSeconds');
    }
    return Math.floor(date.getTime() / 1000);
  }

  /**
   * Convert seconds since 1970 to a Date object
   * @param {number} seconds - Seconds since 1970
   * @returns {Date} Date object
   */
  static secondsToDate(seconds) {
    if (typeof seconds !== 'number' || isNaN(seconds)) {
      throw new Error('Invalid seconds provided to secondsToDate');
    }
    return new Date(seconds * 1000);
  }

  /**
   * Format seconds since 1970 to a readable date string
   * @param {number} seconds - Seconds since 1970
   * @param {string} format - Format type ('short', 'long', 'time', 'datetime')
   * @returns {string} Formatted date string
   */
  static formatSeconds(seconds, format = 'short') {
    const date = this.secondsToDate(seconds);
    return this.formatDate(date, format);
  }

  /**
   * Format a Date object to a readable string
   * @param {Date} date - Date object
   * @param {string} format - Format type ('short', 'long', 'time', 'datetime')
   * @returns {string} Formatted date string
   */
  static formatDate(date, format = 'short') {
    if (!date || !(date instanceof Date)) {
      throw new Error('Invalid date provided to formatDate');
    }

    const pad = n => n.toString().padStart(2, '0');
    
    switch (format) {
      case 'short':
        // Format: DD.MM DDD (e.g., "15.07 Mon")
        const dayName = date.toLocaleDateString(undefined, { weekday: 'short' });
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${dayName}`;
      
      case 'long':
        // Format: DD.MM.YYYY DDD (e.g., "15.07.2024 Mon")
        const longDayName = date.toLocaleDateString(undefined, { weekday: 'short' });
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${longDayName}`;
      
      case 'time':
        // Format: HH:MM (e.g., "14:30")
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
      
      case 'datetime':
        // Format: DD.MM DDD HH:MM (e.g., "15.07 Mon 14:30")
        const datetimeDayName = date.toLocaleDateString(undefined, { weekday: 'short' });
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${datetimeDayName} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
      
      case 'timeRange':
        // Format: HH:MM - HH:MM (for time ranges)
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
      
      default:
        return date.toLocaleDateString();
    }
  }

  /**
   * Format a time range from start and end seconds
   * @param {number} startSeconds - Start time in seconds
   * @param {number} endSeconds - End time in seconds
   * @returns {string} Formatted time range
   */
  static formatTimeRange(startSeconds, endSeconds) {
    if (!startSeconds || !endSeconds) {
      return '';
    }
    
    const startTime = this.formatSeconds(startSeconds, 'time');
    const endTime = this.formatSeconds(endSeconds, 'time');
    return `Godzina: ${startTime} - ${endTime}`;
  }

  /**
   * Get the date key for grouping events by day
   * @param {number} seconds - Seconds since 1970
   * @returns {string} Date key for grouping
   */
  static getDateKey(seconds) {
    const date = this.secondsToDate(seconds);
    return date.toDateString();
  }

  /**
   * Sort dates in ascending order
   * @param {Array} dates - Array of date strings or seconds
   * @param {boolean} isSeconds - Whether the dates are in seconds format
   * @returns {Array} Sorted dates
   */
  static sortDates(dates, isSeconds = false) {
    return dates.sort((a, b) => {
      const dateA = isSeconds ? this.secondsToDate(a) : new Date(a);
      const dateB = isSeconds ? this.secondsToDate(b) : new Date(b);
      return dateA - dateB;
    });
  }

  /**
   * Check if a date is today
   * @param {number} seconds - Seconds since 1970
   * @returns {boolean} True if the date is today
   */
  static isToday(seconds) {
    const date = this.secondsToDate(seconds);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  /**
   * Check if a date is in the future
   * @param {number} seconds - Seconds since 1970
   * @returns {boolean} True if the date is in the future
   */
  static isFuture(seconds) {
    const date = this.secondsToDate(seconds);
    const now = new Date();
    return date > now;
  }

  /**
   * Get current time in seconds since 1970
   * @returns {number} Current time in seconds
   */
  static now() {
    return this.dateToSeconds(new Date());
  }

  /**
   * Create a date from individual components
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @param {number} day - Day (1-31)
   * @param {number} hour - Hour (0-23)
   * @param {number} minute - Minute (0-59)
   * @returns {number} Seconds since 1970
   */
  static createDate(year, month, day, hour = 0, minute = 0) {
    const date = new Date(year, month - 1, day, hour, minute);
    return this.dateToSeconds(date);
  }
} 